import React, { useEffect, useMemo, useState } from "react";
import { FaCalendarAlt, FaCheckCircle, FaClock, FaPlus, FaTrash } from "react-icons/fa";
import TherapistLayout from "./TherapistLayout";
import { therapistAPI } from "../../services/api";
import { handleApiError } from "../../utils/errorUtils";
import "./TherapistPortalPage.css";

const DAY_OPTIONS = [
  { dayOfWeek: 0, label: "Sunday" },
  { dayOfWeek: 1, label: "Monday" },
  { dayOfWeek: 2, label: "Tuesday" },
  { dayOfWeek: 3, label: "Wednesday" },
  { dayOfWeek: 4, label: "Thursday" },
  { dayOfWeek: 5, label: "Friday" },
  { dayOfWeek: 6, label: "Saturday" },
];

const createLocalId = () =>
  `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

const createWindow = (serviceCodes = []) => ({
  localId: createLocalId(),
  startTime: "09:00",
  endTime: "17:00",
  serviceCodes,
});

const createOverride = () => ({
  localId: createLocalId(),
  date: "",
  isUnavailableAllDay: false,
  reason: "",
  isActive: true,
  windows: [createWindow()],
});

const createEmptyAvailabilityRows = () =>
  DAY_OPTIONS.map(({ dayOfWeek, label }) => ({
    dayOfWeek,
    label,
    windows: [],
  }));

const todayString = new Date().toISOString().slice(0, 10);

const mapAvailabilityRows = (availability = []) => {
  const groupedByDay = new Map(DAY_OPTIONS.map(({ dayOfWeek }) => [dayOfWeek, []]));

  availability.forEach((entry) => {
    if (!groupedByDay.has(entry.dayOfWeek)) {
      groupedByDay.set(entry.dayOfWeek, []);
    }

    groupedByDay.get(entry.dayOfWeek).push({
      localId: createLocalId(),
      startTime: entry.startTime || "09:00",
      endTime: entry.endTime || "17:00",
      serviceCodes: entry.serviceCodes || [],
    });
  });

  return DAY_OPTIONS.map(({ dayOfWeek, label }) => ({
    dayOfWeek,
    label,
    windows: (groupedByDay.get(dayOfWeek) || []).sort((left, right) =>
      left.startTime.localeCompare(right.startTime)
    ),
  }));
};

const mapOverrideRows = (overrides = []) =>
  overrides
    .map((override) => ({
      localId: createLocalId(),
      date: override.date ? new Date(override.date).toISOString().slice(0, 10) : "",
      isUnavailableAllDay: Boolean(override.isUnavailableAllDay),
      reason: override.reason || "",
      isActive: override.isActive !== false,
      windows:
        Array.isArray(override.windows) && override.windows.length
          ? override.windows.map((windowEntry) => ({
              localId: createLocalId(),
              startTime: windowEntry.startTime || "09:00",
              endTime: windowEntry.endTime || "17:00",
              serviceCodes: windowEntry.serviceCodes || [],
            }))
          : [createWindow()],
    }))
    .sort((left, right) => left.date.localeCompare(right.date));

const formatDateTime = (value) => {
  if (!value) {
    return "Not scheduled";
  }

  const date = new Date(value);
  return date.toLocaleString([], {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const formatOverrideDate = (value) => {
  if (!value) {
    return "Select a date";
  }

  const date = new Date(`${value}T00:00:00`);
  return date.toLocaleDateString([], {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const getSessionLabel = (sessionType) => {
  switch (sessionType) {
    case "massage_60":
      return "Massage 60";
    case "massage_90":
      return "Massage 90";
    default:
      return sessionType;
  }
};

const TherapistPortalPage = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [availabilityRows, setAvailabilityRows] = useState(createEmptyAvailabilityRows());
  const [availabilityOverrides, setAvailabilityOverrides] = useState([]);
  const [previewDate, setPreviewDate] = useState(todayString);
  const [previewServiceCode, setPreviewServiceCode] = useState("");
  const [previewResult, setPreviewResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingAvailability, setSavingAvailability] = useState(false);
  const [savingOverrides, setSavingOverrides] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const massageServiceOptions = useMemo(
    () =>
      (dashboardData?.serviceOfferings || []).filter(
        (serviceOffering) => serviceOffering.category === "massage"
      ),
    [dashboardData]
  );

  const resetFlashMessages = () => {
    setError("");
    setSuccess("");
  };

  const loadDashboard = async () => {
    try {
      setLoading(true);
      const response = await therapistAPI.getDashboard();
      const nextData = response.data;

      setDashboardData(nextData);
      setAvailabilityRows(mapAvailabilityRows(nextData.availability));
      setAvailabilityOverrides(mapOverrideRows(nextData.overrides));

      const defaultServiceCode =
        nextData.serviceOfferings?.find((service) => service.category === "massage")
          ?.code || "";
      setPreviewServiceCode((currentValue) => currentValue || defaultServiceCode);
    } catch (err) {
      handleApiError(
        err,
        "TherapistPortalPage.loadDashboard",
        setError,
        "Unable to load your therapist portal right now."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboard();
  }, []);

  const addWeeklyWindow = (dayOfWeek) => {
    setAvailabilityRows((currentRows) =>
      currentRows.map((row) =>
        row.dayOfWeek === dayOfWeek
          ? { ...row, windows: [...row.windows, createWindow()] }
          : row
      )
    );
  };

  const updateWeeklyWindow = (dayOfWeek, localId, field, value) => {
    setAvailabilityRows((currentRows) =>
      currentRows.map((row) =>
        row.dayOfWeek === dayOfWeek
          ? {
              ...row,
              windows: row.windows.map((windowEntry) =>
                windowEntry.localId === localId
                  ? { ...windowEntry, [field]: value }
                  : windowEntry
              ),
            }
          : row
      )
    );
  };

  const removeWeeklyWindow = (dayOfWeek, localId) => {
    setAvailabilityRows((currentRows) =>
      currentRows.map((row) =>
        row.dayOfWeek === dayOfWeek
          ? {
              ...row,
              windows: row.windows.filter((windowEntry) => windowEntry.localId !== localId),
            }
          : row
      )
    );
  };

  const toggleWeeklyWindowService = (dayOfWeek, localId, serviceCode) => {
    setAvailabilityRows((currentRows) =>
      currentRows.map((row) =>
        row.dayOfWeek === dayOfWeek
          ? {
              ...row,
              windows: row.windows.map((windowEntry) => {
                if (windowEntry.localId !== localId) {
                  return windowEntry;
                }

                const serviceCodes = windowEntry.serviceCodes.includes(serviceCode)
                  ? windowEntry.serviceCodes.filter((code) => code !== serviceCode)
                  : [...windowEntry.serviceCodes, serviceCode];

                return { ...windowEntry, serviceCodes };
              }),
            }
          : row
      )
    );
  };

  const addOverride = () => {
    setAvailabilityOverrides((currentOverrides) => [...currentOverrides, createOverride()]);
  };

  const updateOverride = (localId, field, value) => {
    setAvailabilityOverrides((currentOverrides) =>
      currentOverrides.map((override) =>
        override.localId === localId
          ? {
              ...override,
              [field]: value,
              windows:
                field === "isUnavailableAllDay" && value
                  ? []
                  : field === "isUnavailableAllDay" &&
                    !value &&
                    !override.windows.length
                  ? [createWindow()]
                  : override.windows,
            }
          : override
      )
    );
  };

  const removeOverride = (localId) => {
    setAvailabilityOverrides((currentOverrides) =>
      currentOverrides.filter((override) => override.localId !== localId)
    );
  };

  const addOverrideWindow = (localId) => {
    setAvailabilityOverrides((currentOverrides) =>
      currentOverrides.map((override) =>
        override.localId === localId
          ? { ...override, windows: [...override.windows, createWindow()] }
          : override
      )
    );
  };

  const updateOverrideWindow = (overrideId, windowId, field, value) => {
    setAvailabilityOverrides((currentOverrides) =>
      currentOverrides.map((override) =>
        override.localId === overrideId
          ? {
              ...override,
              windows: override.windows.map((windowEntry) =>
                windowEntry.localId === windowId
                  ? { ...windowEntry, [field]: value }
                  : windowEntry
              ),
            }
          : override
      )
    );
  };

  const removeOverrideWindow = (overrideId, windowId) => {
    setAvailabilityOverrides((currentOverrides) =>
      currentOverrides.map((override) =>
        override.localId === overrideId
          ? {
              ...override,
              windows: override.windows.filter((windowEntry) => windowEntry.localId !== windowId),
            }
          : override
      )
    );
  };

  const toggleOverrideWindowService = (overrideId, windowId, serviceCode) => {
    setAvailabilityOverrides((currentOverrides) =>
      currentOverrides.map((override) =>
        override.localId === overrideId
          ? {
              ...override,
              windows: override.windows.map((windowEntry) => {
                if (windowEntry.localId !== windowId) {
                  return windowEntry;
                }

                const serviceCodes = windowEntry.serviceCodes.includes(serviceCode)
                  ? windowEntry.serviceCodes.filter((code) => code !== serviceCode)
                  : [...windowEntry.serviceCodes, serviceCode];

                return { ...windowEntry, serviceCodes };
              }),
            }
          : override
      )
    );
  };

  const handleAvailabilitySave = async () => {
    resetFlashMessages();

    try {
      setSavingAvailability(true);
      const availability = availabilityRows.flatMap((row) =>
        row.windows.map((windowEntry) => ({
          dayOfWeek: row.dayOfWeek,
          startTime: windowEntry.startTime,
          endTime: windowEntry.endTime,
          isActive: true,
          serviceCodes: windowEntry.serviceCodes,
        }))
      );

      await therapistAPI.setAvailability({ availability });
      setSuccess("Your weekly availability has been updated.");
      await loadDashboard();
    } catch (err) {
      handleApiError(
        err,
        "TherapistPortalPage.handleAvailabilitySave",
        setError,
        "Unable to save your weekly availability."
      );
    } finally {
      setSavingAvailability(false);
    }
  };

  const handleOverridesSave = async () => {
    resetFlashMessages();

    try {
      setSavingOverrides(true);
      const overrides = availabilityOverrides
        .filter((override) => override.date)
        .map((override) => ({
          date: override.date,
          isUnavailableAllDay: override.isUnavailableAllDay,
          reason: override.reason,
          isActive: override.isActive,
          windows: override.isUnavailableAllDay
            ? []
            : override.windows.map((windowEntry) => ({
                startTime: windowEntry.startTime,
                endTime: windowEntry.endTime,
                serviceCodes: windowEntry.serviceCodes,
              })),
        }));

      await therapistAPI.setAvailabilityOverrides({ overrides });
      setSuccess("Your date-specific overrides have been updated.");
      await loadDashboard();
    } catch (err) {
      handleApiError(
        err,
        "TherapistPortalPage.handleOverridesSave",
        setError,
        "Unable to save your availability overrides."
      );
    } finally {
      setSavingOverrides(false);
    }
  };

  const handlePreview = async () => {
    resetFlashMessages();

    if (!previewDate || !previewServiceCode) {
      setError("Choose a date and massage service before previewing your slots.");
      return;
    }

    try {
      setPreviewLoading(true);
      const response = await therapistAPI.previewAvailability({
        date: previewDate,
        serviceCode: previewServiceCode,
      });
      setPreviewResult(response.data);
    } catch (err) {
      handleApiError(
        err,
        "TherapistPortalPage.handlePreview",
        setError,
        "Unable to preview your massage availability."
      );
    } finally {
      setPreviewLoading(false);
    }
  };

  if (loading) {
    return (
      <TherapistLayout>
        <div className="provider-availability-page">
          <div className="provider-card">
            <p className="provider-empty-state">Loading your therapist portal...</p>
          </div>
        </div>
      </TherapistLayout>
    );
  }

  return (
    <TherapistLayout>
      <div className="therapist-portal-page provider-availability-page">
        <header className="provider-page-header">
          <p className="provider-eyebrow">Massage Therapist</p>
          <h1>Therapist Dashboard</h1>
          <p className="provider-intro">
            Review your massage bookings, keep your recurring hours current, and
            adjust date-specific availability without needing the full staff dashboard.
          </p>
        </header>

        {error && <div className="provider-alert error">{error}</div>}
        {success && <div className="provider-alert success">{success}</div>}

        <section className="therapist-summary-grid">
          <article className="therapist-summary-card">
            <span className="therapist-summary-label">Therapist</span>
            <strong>{dashboardData?.provider?.name || "Unlinked"}</strong>
            <p>{dashboardData?.summary?.nextAppointmentAt ? `Next appointment ${formatDateTime(dashboardData.summary.nextAppointmentAt)}` : "No upcoming appointments scheduled."}</p>
          </article>
          <article className="therapist-summary-card">
            <span className="therapist-summary-label">Upcoming Appointments</span>
            <strong>{dashboardData?.summary?.upcomingCount || 0}</strong>
            <p>Booked, checked-in, or in-progress massage sessions assigned to you.</p>
          </article>
          <article className="therapist-summary-card">
            <span className="therapist-summary-label">Completed in 30 Days</span>
            <strong>{dashboardData?.summary?.completedLast30Days || 0}</strong>
            <p>Recently completed sessions tied to your therapist profile.</p>
          </article>
          <article className="therapist-summary-card">
            <span className="therapist-summary-label">Estimated 30-Day Payout</span>
            <strong>
              {dashboardData?.payoutSummary?.therapistShare
                ? new Intl.NumberFormat("en-US", {
                    style: "currency",
                    currency: "USD",
                  }).format(dashboardData.payoutSummary.therapistShare)
                : "$0.00"}
            </strong>
            <p>
              Based on completed massages from{" "}
              {dashboardData?.payoutSummary?.range?.startDate || "the last 30 days"}.
            </p>
          </article>
        </section>

        <section className="provider-card">
          <div className="provider-card-header">
            <div>
              <h2>Your services</h2>
              <p>These are the massage services currently enabled on your therapist profile.</p>
            </div>
          </div>
          <div className="therapist-service-list">
            {(massageServiceOptions.length
              ? massageServiceOptions.filter((service) =>
                  (dashboardData?.provider?.serviceCapabilities || []).includes(service.code)
                )
              : []
            ).map((service) => (
              <span key={service.code} className="therapist-service-pill">
                {service.name}
              </span>
            ))}
            {!massageServiceOptions.filter((service) =>
              (dashboardData?.provider?.serviceCapabilities || []).includes(service.code)
            ).length && (
              <span className="provider-empty-state">
                No massage services are assigned yet. Ask staff to update your therapist profile.
              </span>
            )}
          </div>
        </section>

        <section className="therapist-booking-grid">
          <article className="provider-card">
            <div className="provider-card-header">
              <div>
                <h2>Upcoming bookings</h2>
                <p>Your next massage sessions in chronological order.</p>
              </div>
            </div>
            {dashboardData?.upcomingBookings?.length ? (
              <div className="therapist-table-wrapper">
                <table className="therapist-bookings-table">
                  <thead>
                    <tr>
                      <th>When</th>
                      <th>Client</th>
                      <th>Service</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dashboardData.upcomingBookings.map((booking) => (
                      <tr key={booking._id}>
                        <td>{formatDateTime(booking.appointmentDate)}</td>
                        <td>
                          <strong>{booking.customer.name}</strong>
                          <span>{booking.customer.contactNumber || booking.customer.email}</span>
                        </td>
                        <td>
                          {getSessionLabel(booking.sessionType)}
                          {booking.suiteAssignment?.number ? ` • Suite #${booking.suiteAssignment.number}` : ""}
                        </td>
                        <td>
                          <span className={`therapist-status-pill ${booking.status}`}>
                            {booking.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="provider-empty-state">No upcoming massage sessions are assigned to you right now.</p>
            )}
          </article>

          <article className="provider-card">
            <div className="provider-card-header">
              <div>
                <h2>Recent bookings</h2>
                <p>Your most recent completed, cancelled, or no-show massage sessions.</p>
              </div>
            </div>
            {dashboardData?.recentBookings?.length ? (
              <div className="therapist-table-wrapper">
                <table className="therapist-bookings-table">
                  <thead>
                    <tr>
                      <th>When</th>
                      <th>Client</th>
                      <th>Service</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dashboardData.recentBookings.map((booking) => (
                      <tr key={booking._id}>
                        <td>{formatDateTime(booking.appointmentDate)}</td>
                        <td>
                          <strong>{booking.customer.name}</strong>
                          <span>{booking.customer.contactNumber || booking.customer.email}</span>
                        </td>
                        <td>{getSessionLabel(booking.sessionType)}</td>
                        <td>
                          <span className={`therapist-status-pill ${booking.status}`}>
                            {booking.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="provider-empty-state">No recent massage session history yet.</p>
            )}
          </article>
        </section>

        <div id="therapist-availability" className="provider-main-stack">
          <section className="provider-card">
            <div className="provider-card-header">
              <div>
                <h2>Weekly recurring hours</h2>
                <p>
                  Keep your recurring massage hours current. Leaving service chips
                  unselected makes that window available for both massage lengths.
                </p>
              </div>
            </div>

            <div className="provider-availability-grid">
              {availabilityRows.map((row) => (
                <article key={row.dayOfWeek} className="provider-availability-row">
                  <div className="provider-day-header provider-day-header-wide">
                    <div>
                      <strong>{row.label}</strong>
                      <p>
                        {row.windows.length
                          ? `${row.windows.length} window${row.windows.length === 1 ? "" : "s"}`
                          : "No recurring availability"}
                      </p>
                    </div>
                    <button
                      type="button"
                      className="provider-secondary-button provider-inline-button"
                      onClick={() => addWeeklyWindow(row.dayOfWeek)}
                    >
                      <FaPlus />
                      Add window
                    </button>
                  </div>

                  {row.windows.length ? (
                    <div className="provider-window-stack">
                      {row.windows.map((windowEntry, index) => (
                        <div key={windowEntry.localId} className="provider-window-card">
                          <div className="provider-window-header">
                            <strong>Window {index + 1}</strong>
                            <button
                              type="button"
                              className="provider-icon-button"
                              onClick={() =>
                                removeWeeklyWindow(row.dayOfWeek, windowEntry.localId)
                              }
                            >
                              <FaTrash />
                            </button>
                          </div>

                          <div className="provider-time-row">
                            <label>
                              Start
                              <input
                                type="time"
                                value={windowEntry.startTime}
                                onChange={(event) =>
                                  updateWeeklyWindow(
                                    row.dayOfWeek,
                                    windowEntry.localId,
                                    "startTime",
                                    event.target.value
                                  )
                                }
                              />
                            </label>
                            <label>
                              End
                              <input
                                type="time"
                                value={windowEntry.endTime}
                                onChange={(event) =>
                                  updateWeeklyWindow(
                                    row.dayOfWeek,
                                    windowEntry.localId,
                                    "endTime",
                                    event.target.value
                                  )
                                }
                              />
                            </label>
                          </div>

                          <div className="provider-service-chip-row">
                            {massageServiceOptions.map((service) => (
                              <button
                                key={`${windowEntry.localId}-${service.code}`}
                                type="button"
                                className={`provider-service-chip ${
                                  windowEntry.serviceCodes.includes(service.code)
                                    ? "selected"
                                    : ""
                                }`}
                                onClick={() =>
                                  toggleWeeklyWindowService(
                                    row.dayOfWeek,
                                    windowEntry.localId,
                                    service.code
                                  )
                                }
                              >
                                {service.name}
                              </button>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="provider-empty-state">
                      No recurring availability for this day yet.
                    </p>
                  )}
                </article>
              ))}
            </div>

            <button
              type="button"
              className="provider-primary-button"
              onClick={handleAvailabilitySave}
              disabled={savingAvailability}
            >
              <FaCheckCircle />
              {savingAvailability ? "Saving..." : "Save weekly availability"}
            </button>
          </section>

          <section className="provider-card">
            <div className="provider-card-header">
              <div>
                <h2>Date-specific overrides</h2>
                <p>Use overrides for vacations, holidays, or one-off custom hours.</p>
              </div>
              <button
                type="button"
                className="provider-secondary-button"
                onClick={addOverride}
              >
                <FaPlus />
                Add override
              </button>
            </div>

            {availabilityOverrides.length ? (
              <div className="provider-window-stack">
                {availabilityOverrides.map((override, index) => (
                  <article
                    key={override.localId}
                    className="provider-window-card provider-override-card"
                  >
                    <div className="provider-window-header">
                      <strong>
                        Override {index + 1}: {formatOverrideDate(override.date)}
                      </strong>
                      <button
                        type="button"
                        className="provider-icon-button"
                        onClick={() => removeOverride(override.localId)}
                      >
                        <FaTrash />
                      </button>
                    </div>

                    <div className="provider-two-column provider-override-grid">
                      <label>
                        Date
                        <input
                          type="date"
                          value={override.date}
                          onChange={(event) =>
                            updateOverride(override.localId, "date", event.target.value)
                          }
                        />
                      </label>
                      <label>
                        Reason
                        <input
                          type="text"
                          value={override.reason}
                          onChange={(event) =>
                            updateOverride(override.localId, "reason", event.target.value)
                          }
                          placeholder="Vacation, holiday, extended day..."
                        />
                      </label>
                    </div>

                    <div className="provider-override-toggle-row">
                      <label className="checkbox-row">
                        <input
                          type="checkbox"
                          checked={override.isUnavailableAllDay}
                          onChange={(event) =>
                            updateOverride(
                              override.localId,
                              "isUnavailableAllDay",
                              event.target.checked
                            )
                          }
                        />
                        <span>Unavailable all day</span>
                      </label>
                      <label className="checkbox-row">
                        <input
                          type="checkbox"
                          checked={override.isActive}
                          onChange={(event) =>
                            updateOverride(
                              override.localId,
                              "isActive",
                              event.target.checked
                            )
                          }
                        />
                        <span>Override is active</span>
                      </label>
                    </div>

                    {!override.isUnavailableAllDay && (
                      <>
                        <div className="provider-window-header provider-subsection-header">
                          <strong>Custom windows</strong>
                          <button
                            type="button"
                            className="provider-secondary-button provider-inline-button"
                            onClick={() => addOverrideWindow(override.localId)}
                          >
                            <FaPlus />
                            Add window
                          </button>
                        </div>

                        <div className="provider-window-stack">
                          {override.windows.map((windowEntry, windowIndex) => (
                            <div
                              key={windowEntry.localId}
                              className="provider-window-card provider-nested-window"
                            >
                              <div className="provider-window-header">
                                <strong>Custom window {windowIndex + 1}</strong>
                                <button
                                  type="button"
                                  className="provider-icon-button"
                                  onClick={() =>
                                    removeOverrideWindow(
                                      override.localId,
                                      windowEntry.localId
                                    )
                                  }
                                >
                                  <FaTrash />
                                </button>
                              </div>

                              <div className="provider-time-row">
                                <label>
                                  Start
                                  <input
                                    type="time"
                                    value={windowEntry.startTime}
                                    onChange={(event) =>
                                      updateOverrideWindow(
                                        override.localId,
                                        windowEntry.localId,
                                        "startTime",
                                        event.target.value
                                      )
                                    }
                                  />
                                </label>
                                <label>
                                  End
                                  <input
                                    type="time"
                                    value={windowEntry.endTime}
                                    onChange={(event) =>
                                      updateOverrideWindow(
                                        override.localId,
                                        windowEntry.localId,
                                        "endTime",
                                        event.target.value
                                      )
                                    }
                                  />
                                </label>
                              </div>

                              <div className="provider-service-chip-row">
                                {massageServiceOptions.map((service) => (
                                  <button
                                    key={`${windowEntry.localId}-${service.code}`}
                                    type="button"
                                    className={`provider-service-chip ${
                                      windowEntry.serviceCodes.includes(service.code)
                                        ? "selected"
                                        : ""
                                    }`}
                                    onClick={() =>
                                      toggleOverrideWindowService(
                                        override.localId,
                                        windowEntry.localId,
                                        service.code
                                      )
                                    }
                                  >
                                    {service.name}
                                  </button>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </article>
                ))}
              </div>
            ) : (
              <p className="provider-empty-state">
                No date-specific overrides yet. Add one for time off or one-off
                schedule changes.
              </p>
            )}

            <button
              type="button"
              className="provider-primary-button"
              onClick={handleOverridesSave}
              disabled={savingOverrides}
            >
              <FaCheckCircle />
              {savingOverrides ? "Saving..." : "Save overrides"}
            </button>
          </section>

          <section className="provider-card">
            <div className="provider-card-header">
              <div>
                <h2>Availability preview</h2>
                <p>Preview the actual bookable slots your current schedule generates.</p>
              </div>
            </div>

            <div className="provider-preview-controls">
              <label>
                Preview date
                <input
                  type="date"
                  value={previewDate}
                  onChange={(event) => setPreviewDate(event.target.value)}
                />
              </label>
              <label>
                Massage service
                <select
                  value={previewServiceCode}
                  onChange={(event) => setPreviewServiceCode(event.target.value)}
                >
                  <option value="">Select a service</option>
                  {massageServiceOptions.map((service) => (
                    <option key={service.code} value={service.code}>
                      {service.name}
                    </option>
                  ))}
                </select>
              </label>
              <button
                type="button"
                className="provider-secondary-button"
                onClick={handlePreview}
                disabled={previewLoading}
              >
                <FaCalendarAlt />
                {previewLoading ? "Previewing..." : "Preview slots"}
              </button>
            </div>

            {previewResult ? (
              <div className="provider-preview-results">
                <div className="provider-preview-summary">
                  <div>
                    <span className="provider-summary-label">Therapist</span>
                    <strong>{previewResult.provider?.name || "Unknown"}</strong>
                  </div>
                  <div>
                    <span className="provider-summary-label">Service</span>
                    <strong>{previewResult.serviceOffering?.name || "Unknown"}</strong>
                  </div>
                  <div>
                    <span className="provider-summary-label">Schedule source</span>
                    <strong>
                      {previewResult.availabilitySource === "override"
                        ? "Date override"
                        : "Weekly schedule"}
                    </strong>
                  </div>
                  <div>
                    <span className="provider-summary-label">Suite</span>
                    <strong>
                      {previewResult.suite ? `#${previewResult.suite.number}` : "No suite"}
                    </strong>
                  </div>
                </div>

                {previewResult.availableSlots?.length ? (
                  <div className="provider-slot-grid">
                    {previewResult.availableSlots.map((slot) => (
                      <div key={slot} className="provider-slot-pill">
                        <FaClock />
                        <span>
                          {new Date(slot).toLocaleTimeString([], {
                            hour: "numeric",
                            minute: "2-digit",
                          })}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="provider-empty-state">
                    No bookable massage slots were generated for that date.
                  </p>
                )}
              </div>
            ) : (
              <p className="provider-empty-state">
                Choose a date and massage service to preview your bookable slots.
              </p>
            )}
          </section>
        </div>
      </div>
    </TherapistLayout>
  );
};

export default TherapistPortalPage;
